/**
 * The ONE factor-enrolment vocabulary (SECREQ S6, PERMISSIONS-CANON-001; coordinator ruling R1, 2026-10-31) — the second
 * factors a tenant sign-in can owe, in the order a hold names them, and the codes every API answers about enrolling one,
 * declared ONCE for the servers that answer them and the clients that read them.
 *
 * A tenant sign-in whose company MFA policy (or an administrator's reset) owes a factor the person does not hold is
 * HELD: 403 with that factor's `*_SETUP_REQUIRED` code and a ten-minute enrolment token for it
 * (`@puffin-core/auth-routes` `signInHold`). Each factor's enrolment accepts that token for its own factor alone
 * (`createFactorEnrollment`), refusing a factor already held (409, `FACTOR_ALREADY_HELD`) and a token older than the
 * route's fresh-proof window (401, `ENROLLMENT_PROOF_STALE`). A completed enrolment that still owes another factor
 * carries the next one's `*_SETUP_REQUIRED` code in its success answer (`enrollmentRequired`).
 *
 * Before this package the codes were written in the server (auth-routes, middleware) and RESTATED in auth-client,
 * login-shell, mfa-ui and palm-native, each its own copy, so a client could key on a code the server no longer sent.
 * Every one of them reads this module now.
 *
 * Data plus one pure reader, zero dependencies; dual-use: CJS source with a GENERATED ESM re-export.
 *
 * @module @puffin-core/factor-enrollment-codes
 */

'use strict';

const { PASSKEY_SETUP_APP_KEYS } = require('./passkeySetupApps');

/**
 * The second factors a tenant enrolment token can enrol, in the order a sign-in hold judges them: two-step
 * verification, then a passkey, then a PIN (a PIN only where it is a credential: pos-api).
 *
 * @constant {ReadonlyArray<string>}
 */
const ENROLLMENT_FACTORS = Object.freeze(['mfa', 'passkey', 'pin']);

/**
 * The hold's code for each factor: the sign-in refusal (403) and a completion's `enrollmentRequired.code`.
 *
 * @constant {Readonly<{mfa: string, passkey: string, pin: string}>}
 */
const FACTOR_SETUP_REQUIRED = Object.freeze({
  mfa: 'MFA_SETUP_REQUIRED',
  passkey: 'PASSKEY_SETUP_REQUIRED',
  pin: 'PIN_SETUP_REQUIRED',
});

/** The status a sign-in hold answers: the credential was right, and the company owes a factor first. */
const FACTOR_SETUP_REQUIRED_STATUS = 403;

/**
 * The enrolment refused because the factor is ALREADY held — a set-up started again, or a completion replayed after
 * its success response was lost — for each factor. The value IS the wire `code`.
 *
 * @constant {Readonly<{mfa: string, passkey: string, pin: string}>}
 */
const FACTOR_ALREADY_HELD = Object.freeze({
  mfa: 'MFA_ALREADY_ENABLED',
  passkey: 'PASSKEY_ALREADY_ENROLLED',
  pin: 'PIN_ALREADY_ENROLLED',
});

/** The status a factor already held answers. */
const FACTOR_ALREADY_HELD_STATUS = 409;

/** The enrolment refused because the sign-in's proof is older than the route's fresh-proof window. */
const ENROLLMENT_PROOF_STALE = 'ENROLLMENT_PROOF_STALE';

/** The status a stale enrolment proof answers: the person signs in again. */
const ENROLLMENT_PROOF_STALE_STATUS = 401;

/**
 * The enrolment refused because the sign-in that minted its token proved too little for the factor: a PIN never stands
 * in for a password, so a PIN sign-in's token cannot register a passkey (SECREQ S6c (b); AUTH-FRESH-001).
 */
const ENROLLMENT_PROOF_INSUFFICIENT = 'ENROLLMENT_PROOF_INSUFFICIENT';

/** The status an insufficient enrolment proof answers: the person signs in with their password. */
const ENROLLMENT_PROOF_INSUFFICIENT_STATUS = 401;

/**
 * A new PIN refused because another person in the company holds it: PINs are unique across the company (owner rulings
 * `109d18383` and `73f5fa7b7`). The value IS the wire `code`. It never says whose PIN it is.
 */
const PIN_IN_USE = 'PIN_IN_USE';

/** The status a PIN in use answers: the person chooses another. */
const PIN_IN_USE_STATUS = 409;

/**
 * A PIN set at sign-in refused because the device is no register yet: setting a PIN is never a device registration, so
 * a person a sign-in held for a PIN sets it only on a register someone already set up (SECREQ S6c (b), D1).
 */
const PIN_TERMINAL_NOT_REGISTERED = 'PIN_TERMINAL_NOT_REGISTERED';

/** The status an unregistered device answers: a manager registers it first. */
const PIN_TERMINAL_NOT_REGISTERED_STATUS = 409;

/**
 * A PIN set on a device that already holds this person's PIN (SECREQ S6c (d)): the PIN IS set there, so no retry can
 * succeed; the person signs in with it. It carried no code, so a step could word it only by guessing.
 */
const PIN_ALREADY_SET_ON_DEVICE = 'PIN_ALREADY_SET_ON_DEVICE';

/** The status a PIN already set on the device answers. */
const PIN_ALREADY_SET_ON_DEVICE_STATUS = 409;

/**
 * A PIN set at sign-in that SET the PIN but could not sign the person in (SECREQ S6c (d)): the PIN is kept, so the
 * person signs in with it. It carried no code, and the held step worded it "could not be set" — which was false.
 */
const PIN_SET_SIGN_IN_FAILED = 'PIN_SET_SIGN_IN_FAILED';

/** The status a PIN set whose sign-in failed answers. */
const PIN_SET_SIGN_IN_FAILED_STATUS = 500;

/**
 * The factor a hold's code names, or null when the code is no hold.
 *
 * @param {*} code - A refusal's `code` (or a completion's `enrollmentRequired.code`), or anything else.
 * @returns {?string} `mfa`, `passkey` or `pin`
 */
function factorOfSetupRequired(code) {
  return ENROLLMENT_FACTORS.find((factor) => FACTOR_SETUP_REQUIRED[factor] === code) ?? null;
}

/**
 * The factor an already-held refusal names, or null when the code is not one.
 *
 * @param {*} code
 * @returns {?string} `mfa`, `passkey` or `pin`
 */
function factorOfAlreadyHeld(code) {
  return ENROLLMENT_FACTORS.find((factor) => FACTOR_ALREADY_HELD[factor] === code) ?? null;
}

module.exports = {
  // SECREQ S6d (owner ruling Q5): the apps that set a passkey up — GENERATED from the mounts, never hand-kept.
  PASSKEY_SETUP_APP_KEYS,
  ENROLLMENT_FACTORS,
  FACTOR_SETUP_REQUIRED,
  FACTOR_SETUP_REQUIRED_STATUS,
  FACTOR_ALREADY_HELD,
  FACTOR_ALREADY_HELD_STATUS,
  ENROLLMENT_PROOF_STALE,
  ENROLLMENT_PROOF_STALE_STATUS,
  ENROLLMENT_PROOF_INSUFFICIENT,
  ENROLLMENT_PROOF_INSUFFICIENT_STATUS,
  PIN_IN_USE,
  PIN_IN_USE_STATUS,
  PIN_TERMINAL_NOT_REGISTERED,
  PIN_TERMINAL_NOT_REGISTERED_STATUS,
  PIN_ALREADY_SET_ON_DEVICE,
  PIN_ALREADY_SET_ON_DEVICE_STATUS,
  PIN_SET_SIGN_IN_FAILED,
  PIN_SET_SIGN_IN_FAILED_STATUS,
  factorOfSetupRequired,
  factorOfAlreadyHeld,
};
